import { useMediaQuery, useTheme } from '@mui/material';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { createContext, type MutableRefObject, type RefObject, useContext } from 'react';

import type { PreviewList } from '~/analytics/events';
import { useHostedInWorkQueueDialog } from '~/components/workQueue/dialogHost';
import { useFeatureFlags } from '~/utils/featureFlags';

import { PeekModeSwitch } from './PeekModeSwitch';
import { useRowPreview } from './useRowPreview';

/** True inside a dialog, where the side panel would open underneath it. */
export const RowPreviewSuppressedContext = createContext(false);

/**
 * The row preview for one table: the hook, the props `LiftAITable` takes and the Peek
 * mode switch for its toolbar. Off unless the `listQuickPreview` flag is on.
 */
export function useTableRowPreview({
  list,
  apiRef,
  rootRef,
  scope = 'table',
  canPreview,
  enabled: enabledHere = true,
}: {
  list: PreviewList;
  apiRef?: MutableRefObject<GridApiPremium>;
  rootRef?: RefObject<HTMLElement | null>;
  scope?: 'page' | 'table';
  canPreview?: (rowId: string) => boolean;
  enabled?: boolean;
}) {
  const { listQuickPreview } = useFeatureFlags();
  const theme = useTheme();
  const canHover = useMediaQuery(theme.breakpoints.up('md'));
  // Inside the Work Queue dialog a side panel would sit under the dialog.
  const hostedInWorkQueueDialog = useHostedInWorkQueueDialog();
  const suppressed = useContext(RowPreviewSuppressedContext);
  const hostedInDialog = hostedInWorkQueueDialog || suppressed;
  const enabled = enabledHere && !hostedInDialog && listQuickPreview === true;

  const rowPreview = useRowPreview({
    list,
    enabled,
    keyboard: canHover,
    scope,
    canPreview,
    apiRef,
    rootRef,
  });

  return {
    enabled,
    rowPreview,
    tableProps: {
      onRowHover: canHover ? rowPreview.onRowHover : undefined,
      onCellKeyDown: rowPreview.onCellKeyDown,
      highlightedRowId: rowPreview.previewId,
    },
    peekSwitch:
      enabled && canHover ? (
        <PeekModeSwitch checked={rowPreview.peekEnabled} onChange={rowPreview.setPeekEnabled} />
      ) : null,
  };
}
